import PageWrapper from '@/components/atoms/PageWrapper';
import { TutorialCard } from '@/features/help/components/organisms/TutorialCard';
import { NavigationBarBreadCrumbs } from "@/components/molecules/BackNav";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { OrganizationTypeWordingResolver } from '@/features/organization/components/atoms/OrganizationTypeWordingResolver';
import { useProjectsList } from '@/lib/useQuery/project/useProjectsList';
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { ChevronRight, CircleHelp, ClipboardPen, FileQuestionMark, FolderTree, UserMinus } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ORGANIZATION_HOMEPAGE_TUTORIALS } from '@/features/help/constants/tutorials';
import { Spinner } from '@/components/ui/spinner';

export default function OrganizationHomePage() {
    const { orgId } = useParams();
    const { organization } = useOrganizationStore();

    const { data: projects, isLoading: isLoadingProjectsList } = useProjectsList({ orgId: Number(orgId) });

    return (
        <PageWrapper>
            <div className="flex flex-col gap-5 grow">
                <NavigationBarBreadCrumbs.Set
                    currentPageName={organization?.name || "Organization"}
                    pages={[
                        {
                            title: "Home",
                            goTo: `/welcome`
                        },

                    ]}
                />

                <div className="flex flex-col flex-wrap h-full gap-3 grow md:flex-row">

                    <div className="flex flex-col flex-1 min-w-[300px] overflow-hidden break-words gap-3">

                        <div className="bg-sidebar border border-gray-300 rounded-lg p-4 flex flex-col gap-3 w-full h-[400px]">
                            <div className="flex items-center gap-1">
                                <CircleHelp size="16" />
                                <h3 className="font-semibold">Getting Started</h3>
                            </div>
                            <ScrollArea className="pe-3" type="always">
                                <div className="flex flex-col gap-2 px-1">
                                    {ORGANIZATION_HOMEPAGE_TUTORIALS.map(tutorial => (
                                        <TutorialCard
                                            key={tutorial.title}
                                            {...tutorial}
                                        />
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>

                        <div className="flex items-center justify-between w-full gap-2 p-4 border border-gray-300 rounded-lg bg-sidebar">
                            <div className="flex flex-col">
                                <div className="flex items-center gap-1 text-sm">
                                    <ClipboardPen size="16" />
                                    <h3 className="font-semibold">Survey Bank</h3>
                                </div>
                                <p className='text-xs text-gray-500'>
                                    Create and manage survey templates to streamline your survey creation process.
                                </p>
                            </div>
                            <Button size="sm" asChild>
                                <Link to={`/organization/${orgId}/manage-organization/survey-bank`}>
                                    Go to <ChevronRight size={16} />
                                </Link>
                            </Button>
                        </div>
                        <div className="flex items-center justify-between w-full gap-2 p-4 border border-gray-300 rounded-lg bg-sidebar">
                            <div className="flex flex-col">
                                <div className="flex items-center gap-1 text-sm">
                                    <FileQuestionMark size="16" />
                                    <h3 className="font-semibold">Question Bank</h3>
                                </div>
                                <p className='text-xs text-gray-500'>
                                    Reuse commonly asked questions across surveys to speed up polling process.
                                </p>
                            </div>
                            <Button size="sm" asChild>
                                <Link to={`/organization/${orgId}/manage-organization/questions-bank`}>
                                    Go to <ChevronRight size={16} />
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <div className="flex-1 min-w-[300px] overflow-hidden break-words">
                        <div className="flex flex-col w-full gap-3 p-4 border border-gray-300 rounded-lg bg-sidebar grow" style={{ height: "calc(100svh - 80px)" }}>
                            {isLoadingProjectsList ? (
                                <div className="flex items-center justify-center w-full h-full">
                                    <Spinner className='size-6'/>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <div className="flex items-center gap-1">
                                            <FolderTree size="16" />
                                            <h3 className="font-semibold"><OrganizationTypeWordingResolver capitalize plural /></h3>
                                        </div>
                                        <div className="text-xs">Organization has {projects?.length || "0"} <OrganizationTypeWordingResolver capitalize plural /></div>
                                    </div>

                                    <ScrollArea className="pe-3" type="always">
                                        <div className="flex flex-col gap-2 px-1">

                                            {projects?.length === 0 && (
                                                <div>
                                                    <div className="text-sm italic text-center text-gray-500">No projects found</div>

                                                </div>
                                            )}

                                            {projects?.map((project) => (
                                                <ProjectElement
                                                    key={project.id}
                                                    title={project.name || ""}
                                                    description={project.challenge || ""}
                                                    projectId={project.id}
                                                />
                                            ))}

                                        </div>
                                    </ScrollArea>
                                </>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </PageWrapper>
    );
}

const ProjectElement = ({
    title,
    description,
    projectId,
}: {
    title: string,
    description: string,
    projectId: string,
}) => {
    const { orgId } = useParams();
    return (
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-white border border-gray-300 rounded-lg">
            <div className="grow">
                <div className="text-sm font-semibold break-words">{title || <span className="italic font-normal text-red-600">Missing name</span>}</div>
                <div className="text-xs text-gray-500 line-clamp-2">{description}</div>
            </div>
            <div className="flex flex-col gap-1">
                <Link to={`/organization/${orgId}/project/${projectId}/manage-project?showMenu=true`}>
                    <Button size="sm">
                        Open <ChevronRight size={16} />
                    </Button>
                </Link>

                {orgId === 'external' && (
                    <Button size="sm" variant="destructive">
                        Leave <UserMinus size={16} />
                    </Button>
                )}
            </div>
        </div>
    )
}
